/**
 * Página /configuracion-notificaciones — Sprint 11 v2 S11-V2-11.
 *
 * Permite al rol Admin (super_admin) ver el histórico de
 * configuraciones del throttle del cron de morosidad y agregar nuevas
 * filas (append-only) sin redeploy.
 *
 * Reglas (validadas por backend):
 * - dias_throttle_mismo_nivel >= 1.
 * - vigente_desde estrictamente posterior a la fila más reciente.
 * - aprobado_acta_url opcional (recomendado para trazabilidad).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../lib/api/client';
import {
  createConfiguracionNotificaciones,
  listConfiguracionesNotificaciones,
} from '../lib/api/configuracion_notificaciones';
import type {
  ConfiguracionNotificaciones,
  ModoRecordatorio,
  NivelMinimoAviso,
} from '../lib/api/types';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { InputFecha } from '../components/ui/InputFecha';
import { PageHeader } from '../components/layout/PageHeader';
import { hoy } from '../lib/tiempo';

const NIVEL_AVISO_LABEL: Record<NivelMinimoAviso, string> = {
  deudor: 'Desde deudor',
  moroso: 'Desde moroso',
  suspendible: 'Solo suspendibles',
};

// Espejo del default del backend (RECORDATORIO_DIA_POR_DEFECTO).
const DIA_RECORDATORIO_POR_DEFECTO = '5';

const MODO_RECORDATORIO_LABEL: Record<ModoRecordatorio, string> = {
  cerrado: 'Cerrado (solo prueba)',
  manual: 'Manual',
  automatico: 'Automático',
};

function formatearFecha(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mes, dia] = m;
  return `${dia}-${mes}-${anio}`;
}

function FilaConfig({
  config,
  esVigente,
}: {
  config: ConfiguracionNotificaciones;
  esVigente: boolean;
}) {
  return (
    <tr className="border-b border-white/[0.08]">
      <td className="px-4 py-3 text-sm">
        {formatearFecha(config.vigente_desde)}
        {esVigente && (
          <span className="ml-2 inline-flex items-center bg-orange/20 text-orange text-[9px] font-medium px-2 py-[2px] rounded-xs uppercase tracking-wide">
            Vigente
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-text-primary font-medium">
        {config.dias_throttle_mismo_nivel} días
      </td>
      <td className="px-4 py-3 text-sm text-text-primary">
        {NIVEL_AVISO_LABEL[config.nivel_minimo_aviso]}
      </td>
      <td className="px-4 py-3 text-sm text-text-primary">
        {config.aviso_al_suspender ? 'Sí' : 'No'}
      </td>
      <td className="px-4 py-3 text-sm text-text-primary">
        {MODO_RECORDATORIO_LABEL[config.recordatorio_semestral_envio]}
        {config.recordatorio_semestral_envio === 'automatico' &&
          ` · día ${config.recordatorio_semestral_dia}`}
      </td>
      <td className="px-4 py-3 text-sm">
        {config.aprobado_acta_url ? (
          <a
            href={config.aprobado_acta_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-admin-red hover:underline"
          >
            Ver acta
          </a>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-text-muted">
        {formatearFecha(config.creado_at.slice(0, 10))}
      </td>
    </tr>
  );
}

interface ModalNuevaProps {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: () => void;
}

function ModalNueva({ abierto, onCerrar, onCreado }: ModalNuevaProps) {
  const [dias, setDias] = useState('7');
  const [nivelAviso, setNivelAviso] = useState<NivelMinimoAviso>('moroso');
  const [avisoAlSuspender, setAvisoAlSuspender] = useState(false);
  const [modoRecordatorio, setModoRecordatorio] =
    useState<ModoRecordatorio>('cerrado');
  const [diaRecordatorio, setDiaRecordatorio] = useState(DIA_RECORDATORIO_POR_DEFECTO);
  const [vigenteDesde, setVigenteDesde] = useState('');
  const [actaUrl, setActaUrl] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await createConfiguracionNotificaciones({
        dias_throttle_mismo_nivel: parseInt(dias, 10),
        nivel_minimo_aviso: nivelAviso,
        aviso_al_suspender: avisoAlSuspender,
        recordatorio_semestral_envio: modoRecordatorio,
        recordatorio_semestral_dia: parseInt(diaRecordatorio, 10),
        vigente_desde: vigenteDesde,
        aprobado_acta_url: actaUrl.trim() || null,
      });
      onCreado();
      onCerrar();
      setDias('7');
      setAvisoAlSuspender(false);
      setModoRecordatorio('cerrado');
      setDiaRecordatorio(DIA_RECORDATORIO_POR_DEFECTO);
      setVigenteDesde('');
      setActaUrl('');
    } catch (err) {
      const mensaje =
        err instanceof ApiError
          ? typeof err.body === 'object' && err.body && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`
          : 'Error de red';
      setError(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  // Default vigente_desde = mañana
  const manana = new Date();
  manana.setDate(manana.getDate() + 1);
  const mananaISO = manana.toISOString().slice(0, 10);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onCerrar}
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-medium text-text-primary mb-2">
          Nueva configuración de notificaciones
        </h2>
        <p className="text-sm text-text-secondary mb-4">
          Esta configuración aplicará al cron de morosidad desde la fecha
          vigente. Los registros anteriores se conservan para auditoría.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="dias"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Días entre recordatorios del mismo nivel
            </label>
            <input
              id="dias"
              type="number"
              min={1}
              required
              value={dias}
              onChange={(e) => setDias(e.target.value)}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30"
            />
            <p className="text-xs text-text-muted mt-1">
              Mínimo 1. Default actual del sistema: 7 días.
            </p>
          </div>

          <div>
            <label
              htmlFor="nivel-aviso"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Avisar al vecino
            </label>
            <select
              id="nivel-aviso"
              value={nivelAviso}
              onChange={(e) => setNivelAviso(e.target.value as NivelMinimoAviso)}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30"
            >
              {(Object.keys(NIVEL_AVISO_LABEL) as NivelMinimoAviso[]).map((n) => (
                <option
                  key={n}
                  value={n}
                >
                  {NIVEL_AVISO_LABEL[n]}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-1">
              Quien está al día nunca recibe aviso. Lo acordado es avisar desde
              deudor.
            </p>
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={avisoAlSuspender}
                onChange={(e) => setAvisoAlSuspender(e.target.checked)}
                className="mt-0.5"
              />
              <span>Avisar al vecino al confirmar una suspensión</span>
            </label>
            <p className="text-xs text-text-muted mt-1">
              Envía el correo y la campana &quot;Servicios suspendidos&quot; con
              la deuda total cuando Tesorería confirma el corte. Queda apagado
              hasta que la Comisión lo valide.
            </p>
          </div>

          <div>
            <label
              htmlFor="modo-recordatorio"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Recordatorio semestral (junio y diciembre)
            </label>
            <select
              id="modo-recordatorio"
              value={modoRecordatorio}
              onChange={(e) =>
                setModoRecordatorio(e.target.value as ModoRecordatorio)
              }
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30"
            >
              {(Object.keys(MODO_RECORDATORIO_LABEL) as ModoRecordatorio[]).map(
                (m) => (
                  <option key={m} value={m}>
                    {MODO_RECORDATORIO_LABEL[m]}
                  </option>
                ),
              )}
            </select>
            <p className="text-xs text-text-muted mt-1">
              Cerrado: solo vista previa y prueba al correo propio. Manual:
              Tesorería lo envía desde Reportes. Automático: además sale solo
              desde el día indicado de junio y diciembre (los días siguientes
              reintentan lo que faltó).
            </p>
          </div>

          {modoRecordatorio === 'automatico' && (
            <div>
              <label
                htmlFor="dia-recordatorio"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                Día del envío automático
              </label>
              <input
                id="dia-recordatorio"
                type="number"
                min={1}
                max={28}
                value={diaRecordatorio}
                onChange={(e) => setDiaRecordatorio(e.target.value)}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="vigente"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Vigente desde
            </label>
            <InputFecha
              id="vigente"
              requerido
              min={mananaISO}
              value={vigenteDesde}
              onChange={setVigenteDesde}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30"
            />
            <p className="text-xs text-text-muted mt-1">
              Debe ser posterior a la fila más reciente.
            </p>
          </div>

          <div>
            <label
              htmlFor="acta"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              URL del acta (opcional)
            </label>
            <input
              id="acta"
              type="url"
              value={actaUrl}
              onChange={(e) => setActaUrl(e.target.value)}
              placeholder="https://drive.calbucura.cl/acta-XYZ.pdf"
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 placeholder:text-text-muted"
            />
          </div>

          {error && (
            <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={enviando || !vigenteDesde}
            >
              {enviando ? 'Creando…' : 'Crear configuración'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ConfiguracionNotificacionesPage() {
  const [configs, setConfigs] = useState<ConfiguracionNotificaciones[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listConfiguracionesNotificaciones()
      .then((data) => {
        if (!cancelado) setConfigs(data);
      })
      .catch((err) => {
        if (cancelado) return;
        const mensaje =
          err instanceof ApiError
            ? `Error ${err.status} al cargar configuraciones`
            : 'Error de red';
        setError(mensaje);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  // Identificar la fila vigente: la más reciente con vigente_desde <= hoy.
  const hoyISO = hoy();
  const vigenteId = configs.find((c) => c.vigente_desde <= hoyISO)?.id;

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Configuración técnica"
        title="Configuración de notificaciones"
        description="Throttle del cron de morosidad — días entre recordatorios del mismo nivel. Tabla append-only para trazabilidad histórica."
        action={
          <Button variant="primary" onClick={() => setModalAbierto(true)}>
            + Nueva configuración
          </Button>
        }
      />

      <Card>
        {loading && <p className="text-sm text-text-muted">Cargando…</p>}
        {error && !loading && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
        {!loading && !error && configs.length === 0 && (
          <p className="text-sm text-text-muted">Sin configuraciones registradas.</p>
        )}
        {!loading && !error && configs.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-[0.5px] border-white/15">
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Vigente desde
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Throttle
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Aviso
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Aviso al suspender
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Recordatorio semestral
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Acta
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase text-text-secondary tracking-wide font-medium">
                  Creado
                </th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <FilaConfig key={c.id} config={c} esVigente={c.id === vigenteId} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ModalNueva
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        onCreado={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
